import UserService from "@/services/api/user";
import type { User, GetUsersParams, GetUsersResponse } from "@/types/user";
import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import type { PayloadAction } from "@reduxjs/toolkit";

interface UserState {
  users: User[];
  totalUsers: number;
  isFetching: boolean;
  isSubmitting: boolean;
  isDownloadingSample: boolean;
  error: string | null;
}

const initialState: UserState = {
  users: [],
  totalUsers: 0,
  isFetching: false,
  isSubmitting: false,
  isDownloadingSample: false,
  error: null,
};

// Async thunk to get users
export const getUsers = createAsyncThunk<
  GetUsersResponse,
  GetUsersParams,
  { rejectValue: string }
>("user/getUsers", async (params, { rejectWithValue }) => {
  try {
    const data: GetUsersResponse = await UserService.getUsers(params);
    return data ?? { rows: [], count: 0 };
  } catch (err) {
    const error = err as { message?: string };
    return rejectWithValue(error.message ?? "Failed to fetch users");
  }
});

// Async thunk to add user
export const addUserThunk = createAsyncThunk<
  User,
  Partial<User>,
  { rejectValue: string }
>("user/addUser", async (userData, { rejectWithValue }) => {
  try {
    const data = await UserService.addUser(userData);
    return data;
  } catch (err: any) {
    const msg = err?.response?.data?.message || err.message || "Failed to add user";
    return rejectWithValue(msg);
  }
});

// Async thunk to update user
export const updateUserThunk = createAsyncThunk<
  User,
  { id: string; data: Partial<User> },
  { rejectValue: string }
>("user/updateUser", async ({ id, data: userData }, { rejectWithValue }) => {
  try {
    const data = await UserService.updateUser(id, userData);
    return data;
  } catch (err: any) {
    const msg = err?.response?.data?.message || err.message || "Failed to update user";
    return rejectWithValue(msg);
  }
});

// Async thunk to delete user
export const deleteUserThunk = createAsyncThunk<
  { id: string | number; message: string },
  string | number,
  { rejectValue: string }
>("user/deleteUser", async (id, { rejectWithValue }) => {
  try {
    const res = await UserService.deleteUser(id);
    return { id, message: res?.message || "User deleted successfully" };
  } catch (err: any) {
    const msg = err?.response?.data?.message || err.message || "Failed to delete user";
    return rejectWithValue(msg);
  }
});

// Async thunk to export sample data
export const exportSampleDataThunk = createAsyncThunk<
  void,
  void,
  { rejectValue: string }
>("user/exportSampleData", async (_, { rejectWithValue }) => {
  try {
    const response = await UserService.exportSampleData();
    const blob = new Blob([response.data], {
      type: response.headers["content-type"] || "application/octet-stream",
    });
    let filename = "sample-import.xlsx";
    const disposition = response.headers["content-disposition"];
    if (disposition && disposition.includes("filename=")) {
      filename = disposition.split("filename=")[1].replace(/"/g, "");
    }
    const link = document.createElement("a");
    link.href = window.URL.createObjectURL(blob);
    link.setAttribute("download", filename);
    document.body.appendChild(link);
    link.click();
    link.remove();
  } catch (error: any) {
    const msg =
      error?.response?.data?.message || "Failed to export excel sample";
    return rejectWithValue(msg);
  }
});

// Slice with builder extraReducers
const userSlice = createSlice({
  name: "user",
  initialState,
  reducers: {
    clear: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      .addCase(getUsers.pending, (state) => {
        state.isFetching = true;
        state.error = null;
      })
      .addCase(
        getUsers.fulfilled,
        (state, action: PayloadAction<GetUsersResponse>) => {
          state.isFetching = false;
          state.users = action.payload?.rows || [];
          state.totalUsers = action.payload?.count || 0;
        }
      )
      .addCase(getUsers.rejected, (state, action) => {
        state.isFetching = false;
        state.error = action.payload as string;
        console.error("Failed to fetch users:", action.payload);
      })
      .addCase(addUserThunk.pending, (state) => {
        state.isSubmitting = true;
        state.error = null;
      })
      .addCase(addUserThunk.fulfilled, (state) => {
        state.isSubmitting = false;
      })
      .addCase(addUserThunk.rejected, (state, action) => {
        state.isSubmitting = false;
        state.error = action.payload as string;
      })
      .addCase(updateUserThunk.pending, (state) => {
        state.isSubmitting = true;
        state.error = null;
      })
      .addCase(updateUserThunk.fulfilled, (state) => {
        state.isSubmitting = false;
      })
      .addCase(updateUserThunk.rejected, (state, action) => {
        state.isSubmitting = false;
        state.error = action.payload as string;
      })
      .addCase(deleteUserThunk.pending, (state) => {
        state.isSubmitting = true;
        state.error = null;
      })
      .addCase(deleteUserThunk.fulfilled, (state, action) => {
        state.isSubmitting = false;
        state.users = state.users.filter((u) => u.id !== action.payload.id);
        state.totalUsers = Math.max(0, state.totalUsers - 1);
      })
      .addCase(deleteUserThunk.rejected, (state, action) => {
        state.isSubmitting = false;
        state.error = action.payload as string;
      })
      .addCase(exportSampleDataThunk.pending, (state) => {
        state.isDownloadingSample = true;
      })
      .addCase(exportSampleDataThunk.fulfilled, (state) => {
        state.isDownloadingSample = false;
      })
      .addCase(exportSampleDataThunk.rejected, (state, action) => {
        state.isDownloadingSample = false;
        console.error("Failed to download sample file:", action.payload);
      });
  },
});

export default userSlice.reducer;
export const { clear } = userSlice.actions;
export type { UserState };
